/**
 * Filter Selector Component
 *
 * Allows users to show/hide filters in the Reports page
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './FilterSelector.css';

const FilterSelector = ({ filters, onFiltersChange, onClose, triggerRef }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside (but not on the trigger button)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (triggerRef?.current && triggerRef.current.contains(event.target)) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, triggerRef]);

  const handleToggleFilter = (filterId) => {
    setLocalFilters(prev =>
      prev.map(filter =>
        filter.id === filterId ? { ...filter, visible: !filter.visible } : filter
      )
    );
  };

  const handleResetToDefault = () => {
    setLocalFilters(prev =>
      prev.map(filter => ({
        ...filter,
        visible: filter.defaultVisible !== undefined ? filter.defaultVisible : filter.visible
      }))
    );
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const visibleCount = localFilters.filter(filter => filter.visible).length;

  return (
    <div className="filter-selector" ref={dropdownRef}>
      <div className="filter-selector__header">
        <h3 className="filter-selector__title">Manage Filters</h3>
        <button
          className="filter-selector__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="filter-selector__info">
        {visibleCount} of {localFilters.length} filters visible
      </div>

      <div className="filter-selector__list">
        {localFilters.map((filter) => (
          <label
            key={filter.id}
            className={`filter-selector__item ${filter.required ? 'filter-selector__item--required' : ''}`}
          >
            <input
              type="checkbox"
              checked={filter.visible}
              disabled={filter.required}
              onChange={() => handleToggleFilter(filter.id)}
              className="filter-selector__checkbox"
            />
            <span className="filter-selector__label">
              {filter.label}
              {filter.required && (
                <span className="filter-selector__required"> (required)</span>
              )}
            </span>
          </label>
        ))}
      </div>

      <div className="filter-selector__actions">
        <button
          className="filter-selector__button filter-selector__button--secondary"
          onClick={handleResetToDefault}
        >
          Reset to Default
        </button>
        <button
          className="filter-selector__button filter-selector__button--primary"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

FilterSelector.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      required: PropTypes.bool,
      visible: PropTypes.bool.isRequired,
      defaultVisible: PropTypes.bool,
    })
  ).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  triggerRef: PropTypes.object,
};

export default FilterSelector;
